/**
 * Provider catalog.
 *
 * Five providers are supported: the Anthropic Messages API (API key), the
 * locally installed Claude Code CLI, the locally installed Codex agent, and two
 * OpenAI-compatible gateways (Logfare, Pollinations) that take a bearer key,
 * plus a keyless local model served by Ollama on this machine ('local').
 * Everything downstream consumes a `ResolvedProvider`.
 */

export const PROVIDER_IDS = [
  'anthropic',
  'claudecli',
  'codex',
  'antigravity',
  'grok',
  'logfare',
  'pollinations',
  'local',
] as const;

export type ProviderId = (typeof PROVIDER_IDS)[number];

/** How a provider authenticates. */
export type AuthStyle =
  /** Anthropic Messages API with x-api-key + anthropic-version. */
  | 'anthropic-x'
  /** OpenAI-compatible /chat/completions with a bearer key. */
  | 'bearer'
  /** A local runtime that signs in on its own; nothing to store. */
  | 'local';

export type Capability = 'chat' | 'reasoning';

export interface ProviderSpec {
  readonly id: ProviderId;
  readonly label: string;
  /** Base for chat calls, or a `local://` marker for runtimes spawned on this machine. */
  readonly chatBaseUrl: string;
  /**
   * Environment variable that holds the credential.
   *
   * Null when the provider never takes a key (the local CLIs sign in on their own).
   */
  readonly envKey: string | null;
  readonly auth: AuthStyle;
  readonly capabilities: readonly Capability[];
  readonly notes?: string;
}

/** Where the local runtime (Ollama) listens unless LOCAL_LLM_BASE_URL says otherwise. */
export const DEFAULT_LOCAL_LLM_BASE_URL = 'http://127.0.0.1:11434/v1';
/** Default local model: abliterated Qwen3.5 9B (tools + thinking), Q4_K, ~6.6 GB. */
export const DEFAULT_LOCAL_MODEL = 'huihui_ai/qwen3.5-abliterated:9b';
/** Lighter, faster local model: abliterated Qwen3 4B Instruct 2507 (no thinking), Q4_K_M, ~2.5 GB. */
export const LIGHT_LOCAL_MODEL = 'huihui_ai/qwen3-abliterated:4b-instruct-2507-q4_K_M';

export const PROVIDERS: Record<ProviderId, ProviderSpec> = {
  local: {
    id: 'local',
    label: 'Local model (uncensored)',
    // Ollama's OpenAI-compatible endpoint; override with LOCAL_LLM_BASE_URL or the Providers panel.
    chatBaseUrl: DEFAULT_LOCAL_LLM_BASE_URL,
    envKey: null,
    auth: 'local',
    capabilities: ['chat', 'reasoning'],
    notes:
      'Runs an open-weights model on this GPU through Ollama (E:\\LocalLLM), started automatically ' +
      'when needed. No key, no cloud, no content filter. Models are listed from the runtime. ' +
      'The model is unloaded from VRAM before local 3D/audio/pixel generation runs and reloads on the next message.',
  },

  logfare: {
    id: 'logfare',
    label: 'Logfare',
    // https://logfare.ai/docs : OpenAI-compatible, Bearer auth, public GET /v1/models.
    chatBaseUrl: 'https://logfare.ai/v1',
    envKey: 'LOGFARE_API_KEY',
    auth: 'bearer',
    capabilities: ['chat', 'reasoning'],
    notes:
      'OpenAI-compatible gateway. Tool calling is not documented for every model; if a model ' +
      'rejects native tools the agent falls back to a text tool protocol. Rate limited per account (429 + Retry-After).',
  },

  pollinations: {
    id: 'pollinations',
    label: 'Pollinations',
    // https://gen.pollinations.ai/docs : OpenAI-compatible, Bearer sk_ key, public GET /v1/models.
    chatBaseUrl: 'https://gen.pollinations.ai/v1',
    envKey: 'POLLINATIONS_API_KEY',
    auth: 'bearer',
    capabilities: ['chat', 'reasoning'],
    notes:
      'OpenAI-compatible chat. Pick a model whose capabilities include tool_calling for agents; ' +
      'models are listed live from the gateway. Use a secret (sk_) key.',
  },

  anthropic: {
    id: 'anthropic',
    label: 'Anthropic',
    chatBaseUrl: 'https://api.anthropic.com/v1',
    envKey: 'ANTHROPIC_API_KEY',
    auth: 'anthropic-x',
    capabilities: ['chat', 'reasoning'],
    notes:
      'Messages API. The member effort level is sent where the model supports it (Opus 4.5 and ' +
      'later), clamped to its levels. Thinking is adaptive on 4.6 and later models, a token budget on older ones.',
  },

  claudecli: {
    id: 'claudecli',
    label: 'Claude CLI',
    // Not an endpoint: the local `claude` binary is spawned.
    chatBaseUrl: 'local://claude-cli',
    envKey: null,
    auth: 'local',
    capabilities: ['chat', 'reasoning'],
    notes:
      'Runs the real Claude Code installed on this machine as a full agent, outside the Prism Engine Cloud ' +
      'harness: its own file, shell, web and subagent tools, with permissions bypassed. Prism Engine Cloud ' +
      'adds the board and delegation tools (as MCP). Uses your own `claude` login, no API key ' +
      '(set CLAUDE_CLI_PATH to use a specific binary).',
  },

  codex: {
    id: 'codex',
    label: 'Codex',
    chatBaseUrl: 'local://codex',
    envKey: null,
    auth: 'local',
    capabilities: ['chat', 'reasoning'],
    notes:
      'Runs the installed Codex agent with its own tools, context management and sandbox, outside the Prism Engine Cloud harness. Sign in with codex login on this machine. Set CODEX_CLI_PATH to select a binary.',
  },

  antigravity: {
    id: 'antigravity',
    label: 'Antigravity CLI',
    chatBaseUrl: 'local://antigravity-cli',
    envKey: null,
    auth: 'local',
    capabilities: ['chat', 'reasoning'],
    notes:
      'Runs the locally installed Antigravity CLI (`agy`) as a full agent with MCP board and shared memory tools. You can choose whether to run Antigravity with AGY_ADC_AUTH=true (Google Cloud Application Default Credentials) or your standard Antigravity login. Set AGY_CLI_PATH to select a binary.',
  },

  grok: {
    id: 'grok',
    label: 'Grok CLI',
    chatBaseUrl: 'local://grok-cli',
    envKey: null,
    auth: 'local',
    capabilities: ['chat', 'reasoning'],
    notes:
      'Runs the locally installed Grok CLI (`grok`) as a full agent with MCP board and shared memory tools, sharing the exact same session and memory system as every other provider. Sign in with `grok` on this machine (or set GROK_CLI_PATH).',
  },
};

export function isProviderId(value: unknown): value is ProviderId {
  return typeof value === 'string' && (PROVIDER_IDS as readonly string[]).includes(value);
}

/** A model a provider accepts, with the name to show for it. */
export interface ProviderModel {
  readonly id: string;
  readonly label: string;
}

/** A provider flattened into everything a chat call needs. */
export interface ResolvedProvider {
  readonly id: string;
  readonly label: string;
  readonly chatBaseUrl: string;
  readonly auth: AuthStyle;
  readonly capabilities: readonly Capability[];
  /** False when the provider works with no key at all (the local CLIs). */
  readonly requiresCredential: boolean;
}

/** Providers that sign in on this machine (or run on it) and never store an API key. */
const LOCAL_PROVIDER_IDS: ReadonlySet<string> = new Set([
  'local',
  'claudecli',
  'codex',
  'antigravity',
  'grok',
]);

/** True unless the provider authenticates with a credential the user must supply. */
export function providerRequiresCredential(id: ProviderId): boolean {
  return !LOCAL_PROVIDER_IDS.has(id);
}

/** True when a provider can store or select an API key. */
export function providerAcceptsApiKey(id: string): boolean {
  return !LOCAL_PROVIDER_IDS.has(id);
}

/** Flatten a catalogued provider into the runtime shape. */
export function resolveBuiltin(id: ProviderId): ResolvedProvider {
  const spec = PROVIDERS[id];
  return {
    id,
    label: spec.label,
    chatBaseUrl: spec.chatBaseUrl,
    auth: spec.auth,
    capabilities: spec.capabilities,
    requiresCredential: providerRequiresCredential(id),
  };
}

/**
 * How a provider keeps its own history. The canonical session record is shared by
 * all of them; a family only decides which native session (if any) is reused.
 */
export type SessionFamily = 'claudecli' | 'codex' | 'antigravity' | 'grok' | 'generic';

export function sessionFamilyOf(provider: string | null | undefined): SessionFamily {
  return provider === 'claudecli' ||
    provider === 'codex' ||
    provider === 'antigravity' ||
    provider === 'grok'
    ? provider
    : 'generic';
}

/** Models offered in the roster picker. Codex models are listed from the CLI at runtime. */
export const SUGGESTED_MODELS: Record<ProviderId, readonly string[]> = {
  anthropic: ['claude-sonnet-4-5', 'claude-opus-4-1', 'claude-haiku-4-5'],
  claudecli: ['sonnet', 'opus', 'haiku', 'fable'],
  codex: ['default'],
  antigravity: [
    'gemini-4-argon-high',
    'gemini-3.8-flash-high',
    'claude-sonnet-5-5-high',
    'claude-opus-5-5-high',
  ],
  grok: ['grok-4.7', 'grok-build', 'grok-4.6'],
  logfare: ['logfare/auto', 'gemma-4-26b', 'deepseek-v4.1-flash', 'qwen-3.8-27b'],
  pollinations: [],
  local: [DEFAULT_LOCAL_MODEL, LIGHT_LOCAL_MODEL],
};

/** Providers whose catalog can be fetched live from a public /models endpoint. */
export const LIVE_MODEL_LIST_URLS: Partial<Record<ProviderId, string>> = {
  logfare: 'https://logfare.ai/v1/models',
  pollinations: 'https://gen.pollinations.ai/v1/models',
  // Default address; ai-sdk resolves the configured base URL at call time.
  local: `${DEFAULT_LOCAL_LLM_BASE_URL}/models`,
};

/** A model as listed by a gateway, normalised across providers. */
export interface ListedModel {
  readonly id: string;
  readonly label: string;
  /** True / false when the gateway says; null when it does not say. */
  readonly tools: boolean | null;
  readonly paidOnly?: boolean;
}

/**
 * Normalise a /v1/models payload into chat-capable models.
 *
 * Logfare lists every modality; chat models carry "chat/completions" in
 * `endpoints`. Pollinations lists `category: "text"` models with a `tools` flag
 * and `capabilities`.
 */
export function parseModelList(provider: ProviderId, payload: unknown): ListedModel[] {
  const data = (payload as { data?: unknown })?.data;
  if (!Array.isArray(data)) return [];
  const out: ListedModel[] = [];
  for (const raw of data as Array<Record<string, unknown>>) {
    const id = typeof raw?.id === 'string' ? raw.id : '';
    if (!id) continue;
    if (provider === 'logfare') {
      const endpoints = Array.isArray(raw.endpoints) ? (raw.endpoints as unknown[]) : [];
      if (!endpoints.includes('chat/completions')) continue;
      out.push({
        id,
        label: typeof raw.display_name === 'string' ? raw.display_name : id,
        tools: null,
      });
    } else if (provider === 'local') {
      // Ollama lists every pulled model; embedding models cannot chat.
      if (/embed/i.test(id)) continue;
      out.push({ id, label: id, tools: null });
    } else if (provider === 'pollinations') {
      if (raw.category !== undefined && raw.category !== 'text') continue;
      const endpoints = Array.isArray(raw.supported_endpoints)
        ? (raw.supported_endpoints as unknown[])
        : ['/v1/chat/completions'];
      if (!endpoints.includes('/v1/chat/completions')) continue;
      const caps = Array.isArray(raw.capabilities) ? (raw.capabilities as unknown[]) : [];
      out.push({
        id,
        label: typeof raw.title === 'string' ? raw.title : id,
        tools: raw.tools === true || caps.includes('tool_calling') ? true : raw.tools === false ? false : null,
        paidOnly: raw.paid_only === true,
      });
    }
  }
  return out;
}

/** Models to offer for a provider. */
export function modelsForProvider(providerId: string | null): readonly string[] {
  if (!providerId) return [];
  if (isProviderId(providerId)) return SUGGESTED_MODELS[providerId] ?? [];
  return [];
}
